import '../CommonCommericalSurvey/CustomExpression';
import '../CommonCommericalSurvey/CustomProperties';
import '../CommonCommericalSurvey/CustomWidgets';
import '../CommonCommericalSurvey/CommercialSurvey.css';

import { useApolloClient } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import { Model } from 'survey-core';
import { Survey } from 'survey-react-ui';

import Loading from '../../components/Loading/Loading';
import Navigation from '../../components/Navigation/Navigation';
import {
  ApplicationResultKind,
  GetApplicationGroupDocument,
  GetApplicationGroupQuery,
  GetApplicationGroupQueryVariables,
  GetLobShellStateDocument,
  GetLobShellStateQuery,
  GetLobShellStateQueryVariables,
  GetThirdJsonDocument,
  GetThirdJsonQuery,
  GetThirdJsonQueryVariables,
  useCompleteApplicationMutation,
  useCreateLobApplicationMutation
} from '../../generated/graphql';
import { getSiblingCoverageKeys } from '../../utils/lobCoverage';
import { formatDateAnswers } from '../../utils/SurveyJSQuestion';
import { SurveyCSS } from '../CommonCommericalSurvey/commonVariables';

interface LobShellState {
  phase: 3;
  fetchThirdJSONs: string[];
  carrierPartner: string;
  phase2LastPageName: string;
  /**
   * Filename of the Phase 2 (biz_coverage / pre-split combined) JSON the user
   * just left. Forwarded as `jsonName` to the fallback completeApplication
   * call when Phase 3 JSON fails to load. Optional for back-compat with
   * navigation state from older frontend builds.
   */
  phase2JsonFileName?: string;
  country: string;
  province: string;
  // One Application id per Phase 3 LOB in fetchThirdJSONs; index 0 is the
  // entry app (lobType depends on Phase 2 selection). Optional for back-compat.
  lobApplicationIds?: string[];
  currentLobIndex?: number;
}

type RecoveryStatus = 'idle' | 'loading' | 'failed';

// A LOB's saved answers must not carry the sibling LOB's coverage toggle — it
// rides in from previousAnswers/phase2 (the parent Phase 2 selected both). Drop
// it so each per-LOB Application/Policy stores only its own coverage. Keeps
// endorsement inheritance single-LOB too.
const stripSiblingCoverage = (
  answers: Record<string, unknown>,
  lob: string
): void => {
  getSiblingCoverageKeys(lob).forEach((key) => {
    delete answers[key];
  });
};

const LobShell: React.FC = () => {
  const location = useLocation();
  const history = useHistory();
  const { lobType, applicationId } = useParams<{
    lobType: string;
    applicationId: string;
  }>();
  const apolloClient = useApolloClient();

  // State source: router state only when present — just the internal LOB→LOB
  // hops pass it forward. Otherwise (first entry from Phase 2, and cold-loads:
  // refresh / pasted URL / email link) derive it from the backend via getLobShellState.
  const stateFromHistory = location.state as LobShellState | null;
  const [recoveredState, setRecoveredState] = useState<LobShellState | null>(
    null
  );
  const [recoveryStatus, setRecoveryStatus] = useState<RecoveryStatus>(
    stateFromHistory ? 'idle' : 'loading'
  );

  useEffect(() => {
    if (stateFromHistory) return;
    let cancelled = false;
    apolloClient
      .query<GetLobShellStateQuery, GetLobShellStateQueryVariables>({
        query: GetLobShellStateDocument,
        variables: { applicationId },
        fetchPolicy: 'network-only'
      })
      .then(({ data }) => {
        if (cancelled) return;
        const recovered = data?.getLobShellState;
        if (recovered) {
          setRecoveredState({
            ...recovered,
            phase: 3,
            lobApplicationIds: recovered.lobApplicationIds.map((id: unknown) =>
              String(id)
            )
          });
          setRecoveryStatus('idle');
        } else {
          setRecoveryStatus('failed');
        }
      })
      .catch(() => {
        if (!cancelled) setRecoveryStatus('failed');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  const lobShellState: LobShellState | null =
    stateFromHistory ?? recoveredState;
  const {
    fetchThirdJSONs = [],
    carrierPartner = '',
    phase2LastPageName = '',
    phase2JsonFileName,
    country = '',
    province = '',
    lobApplicationIds = [applicationId],
    currentLobIndex = 0
  } = lobShellState ?? {};
  // isLastLob is derived from the queue of Phase 3 JSON filenames, not the
  // list of already-seeded application ids — additional LOBs are seeded
  // lazily after each completeApplication, so lobApplicationIds.length
  // <= fetchThirdJSONs.length until the very last hop.
  const isLastLob = currentLobIndex + 1 >= fetchThirdJSONs.length;
  const currentLobJsonFileName: string | undefined =
    fetchThirdJSONs[currentLobIndex];

  interface QueryState {
    thirdJson: Record<string, unknown> | null;
    previousAnswers: Record<string, unknown> | null;
    phase2Answers: Record<string, unknown>;
    isLoading: boolean;
  }
  const [queryState, setQueryState] = useState<QueryState>({
    thirdJson: null,
    previousAnswers: null,
    phase2Answers: {},
    isLoading: true
  });
  const {
    thirdJson,
    previousAnswers,
    phase2Answers,
    isLoading: isLoadingJson
  } = queryState;

  // Fetch Phase 3 JSON whenever the LOB target changes — covers multi-LOB
  // transitions where LobShell re-renders with a new applicationId (and
  // currentLobIndex) without unmounting. Empty-deps would leave thirdJson
  // pinned to the previous LOB's JSON and the SurveyJS model stuck in its
  // post-completion state. Skipped while currentLobJsonFileName is
  // undefined (state is still being recovered from the backend on a cold load).
  useEffect(() => {
    if (!currentLobJsonFileName) return;
    // LobShell stays mounted across LOB transitions — clear the navigation
    // flag so the new LOB's render doesn't stay stuck on Loading.
    setIsNavigatingToNextLob(false);
    setQueryState({
      thirdJson: null,
      previousAnswers: null,
      phase2Answers: {},
      isLoading: true
    });
    apolloClient
      .query<GetThirdJsonQuery, GetThirdJsonQueryVariables>({
        query: GetThirdJsonDocument,
        variables: { applicationId, thirdJsonFileName: currentLobJsonFileName },
        fetchPolicy: 'network-only'
      })
      .then(({ data }) => {
        const response = data?.getThirdJson ?? null;
        const json = response?.json ?? null;
        const prevAnswers = response?.previousAnswers ?? null;
        const secondJsonAnswers =
          (response?.answersFromSecondJson as Record<string, unknown> | null) ??
          {};
        setQueryState({
          thirdJson: json,
          previousAnswers: prevAnswers,
          phase2Answers: secondJsonAnswers,
          isLoading: false
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, currentLobJsonFileName]);

  // Reason Flag is part of Phase 2's stored answers — derive it instead of
  // threading it through router state.
  const phase2ReasonFlag = phase2Answers['Reason Flag'] as string | undefined;

  const model = useMemo(() => {
    if (!thirdJson) return null;
    const m = new Model(thirdJson);
    m.setVariable('carrierPartner', carrierPartner);
    // Inject flags as variables — they affect visibleIf but are NOT included in model.data,
    // so they won't be sent to completeApplication (backend rejects answers containing flag fields)
    m.setVariable(
      'BusinessInformation_MunichProduct',
      phase2Answers['BusinessInformation_MunichProduct']
    );
    m.setVariable(
      'BusinessInformation_Broker',
      phase2Answers['BusinessInformation_Broker']
    );

    // Endorsement/renewal: pre-fill Munich page answers from the prior policy.
    // Same pattern as CommercialWithApplicationId for Phase 2 — strip first-page
    // fields (set fresh on the new application) and inject the rest. Phase 2
    // answers are applied afterwards so current-application values win.
    if (previousAnswers) {
      const {
        /* eslint-disable @typescript-eslint/no-unused-vars */
        BusinessInformation_100_EffectiveDate_WORLD_EN: _effectiveDate,
        BusinessInformation_100_Profession_WORLD_EN: _profession,
        BusinessInformation_100_CustomerInfo_WORLD_EN: _customerInfo,
        BusinessInformation_100_State_WORLD_EN: _state,
        /* eslint-enable @typescript-eslint/no-unused-vars */
        ...previousAnswersWithoutFirstPage
      } = previousAnswers;
      m.setVariable('isEndorsement', true);
      m.setDataCore({ ...m.data, ...previousAnswersWithoutFirstPage });
    }

    // Strip flags that must not reach completeApplication (backend rejects them).
    // Munich_9219_QuoteFlag is NOT stripped here — it is injected via phase2AnswersWithoutFlags
    // so Phase 3 triggers start with the Phase 2-computed value. The Phase 3 JSON defines it
    // as visible:false, so clearInvisibleValues:"onComplete" removes it from model.data
    // automatically before completeApplication is called.
    const {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      BusinessInformation_MunichProduct: _munichProduct,
      BusinessInformation_Broker: _broker,
      Beazley_5415_QuoteFlag: _beazleyFlag,
      /* eslint-enable @typescript-eslint/no-unused-vars */
      ...phase2AnswersWithoutFlags
    } = phase2Answers;
    // Use setDataCore + runTriggers (not direct m.data assignment) so that
    // runexpression triggers fire — in particular the workExclusionVariableText
    // trigger that computes Munich_PersonalServices_9219_WorkExclusionListHtml_WORLD_EN.
    m.setDataCore({ ...m.data, ...phase2AnswersWithoutFlags });
    m.runTriggers();

    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thirdJson]);

  // Endorsements are single-LOB → never seed a sibling (mirrors Phase 2 guard).
  const isEndorsement = !!model?.getVariable('isEndorsement');

  const [isComplete, setComplete] = useState(false);
  const [isRedirectToQuote, setRedirectToQuote] = useState(false);
  const [isNavigatingToNextLob, setIsNavigatingToNextLob] = useState(false);
  // Captured from the completeApplication response so the final redirect
  // goes to `/quote/<applicationGroupId>` per master's PR #1153 route rename.
  const [
    completedApplicationGroupId,
    setCompletedApplicationGroupId
  ] = useState<string | null>(null);

  const [
    completeApplicationMutation,
    { loading: isCompleting }
  ] = useCompleteApplicationMutation();
  const [
    createLobApplicationMutation,
    { loading: isSeedingNextLob }
  ] = useCreateLobApplicationMutation();

  // Cold-load recovery render gate: keep rendering Loading while the backend
  // fills in the LobShell state, redirect home if it can't (no group, missing
  // lobType on a child, etc.). Single-flow (state-from-history) renders skip
  // this branch entirely — `recoveryStatus` stays 'idle' there.
  if (recoveryStatus === 'loading') return <Loading />;
  if (recoveryStatus === 'failed' || !lobShellState) {
    return <Redirect to="/" />;
  }

  if (
    isLoadingJson ||
    isCompleting ||
    isSeedingNextLob ||
    isNavigatingToNextLob
  )
    return <Loading />;

  if (isComplete) {
    if (isRedirectToQuote) {
      if (!completedApplicationGroupId) {
        throw new Error(
          'applicationGroupId missing from completeApplication response'
        );
      }
      return (
        <Redirect to={{ pathname: `/quote/${completedApplicationGroupId}` }} />
      );
    }
    return (
      <Redirect to={{ pathname: '/complete', state: { country, province } }} />
    );
  }

  // Fallback: getThirdJson returned null (edge case — e.g. sendToBrokerFlag toggled between
  // Phase 2 submit and Phase 3 load). Call completeApplication directly.
  if (!thirdJson || !model) {
    const {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      BusinessInformation_MunichProduct: _munichProduct,
      Munich_9219_QuoteFlag: _munichFlag,
      BusinessInformation_Broker: _broker,
      Beazley_5415_QuoteFlag: _beazleyFlag,
      /* eslint-enable @typescript-eslint/no-unused-vars */
      ...answersWithoutFlags
    } = phase2Answers;

    // Completing from Phase 2 answers — keep single-LOB (drop sibling coverage).
    stripSiblingCoverage(answersWithoutFlags, lobType);

    completeApplicationMutation({
      variables: {
        answersInfo: {
          applicationId,
          answers: answersWithoutFlags,
          isBrokerFlow: !!(
            phase2Answers['BusinessInformation_Broker'] ||
            !phase2Answers['Munich_9219_QuoteFlag']
          ),
          reasonFlag: phase2ReasonFlag
        },
        pageName: phase2LastPageName,
        // Phase 3 JSON failed to load — we're completing from the Phase 2
        // answer state, so the row's source JSON is the biz_coverage filename.
        jsonName: phase2JsonFileName
      }
    }).then(async ({ data: result }) => {
      if (!result) throw new Error('completeApplicationData should be ready');
      const {
        errors,
        kind,
        applicationGroupId: resultApplicationGroupId
      } = result.completeApplication;
      if (errors.length > 0) throw new Error(errors.join(' - '));
      if (
        kind === ApplicationResultKind.OfferQuote &&
        !isLastLob &&
        !isEndorsement
      ) {
        const nextIndex = currentLobIndex + 1;
        // TODO: remove seedNextLob fallback once upfront-seeding is rolled out
        // and no in-flight pre-deploy users remain.
        let nextApplicationId = lobApplicationIds[nextIndex];
        if (!nextApplicationId) {
          nextApplicationId = await seedNextLob(
            resultApplicationGroupId,
            fetchThirdJSONs[nextIndex]
          );
        }
        setIsNavigatingToNextLob(true);
        const { data: groupData } = await apolloClient.query<
          GetApplicationGroupQuery,
          GetApplicationGroupQueryVariables
        >({
          query: GetApplicationGroupDocument,
          variables: { id: nextApplicationId },
          fetchPolicy: 'network-only'
        });
        const nextLobType =
          groupData?.getApplicationGroup?.applications.find(
            (a) => a.applicationId.toString() === nextApplicationId.toString()
          )?.lobType ?? '';
        history.push(`/${nextLobType}/${nextApplicationId}`, {
          ...lobShellState,
          lobApplicationIds:
            lobApplicationIds.length > nextIndex
              ? lobApplicationIds
              : [...lobApplicationIds, nextApplicationId],
          currentLobIndex: nextIndex
        });
        return;
      }
      setCompletedApplicationGroupId(resultApplicationGroupId);
      if (kind === ApplicationResultKind.OfferQuote) {
        setRedirectToQuote(true);
      }
      setComplete(true);
    });

    return <Loading />;
  }

  // Seeds the next LOB's Application + ApplicationAnswers via the backend
  // mutation. Called between the current LOB's completeApplication and the
  // navigation to the next LobShell so each Application's create/update/
  // complete rows land contiguously in time.
  const seedNextLob = async (
    applicationGroupId: string,
    jsonFileName: string
  ): Promise<string> => {
    const { data: seedResult } = await createLobApplicationMutation({
      variables: { applicationGroupId, jsonFileName }
    });
    const outcome = seedResult?.createLobApplication;
    if (outcome?.__typename !== 'ApplicationSuccess') {
      throw new Error(
        outcome?.__typename === 'ApplicationFailure'
          ? outcome.error
          : 'Failed to seed next LOB application'
      );
    }
    return outcome.applicationId;
  };

  // Munich path: show the LOB survey page
  const doComplete = async () => {
    // Capture fields before completeLastPage() — SurveyJS's default
    // clearInvisibleValues:"onComplete" removes invisible questions from model.data.
    const preCompleteMunichFlag = model.data['Munich_9219_QuoteFlag'];
    const preCompleteReasonFlag =
      (model.data['Reason Flag'] as string | undefined) || phase2ReasonFlag;
    // WorkExclusionListHtml is visible:false in Phase 3 JSON → cleared by
    // clearInvisibleValues:"onComplete". Capture before completeLastPage().
    const preCompleteWorkExclusionListHtml =
      model.data['Munich_PersonalServices_9219_WorkExclusionListHtml_WORLD_EN'];
    // WorkExclusionText is an html-type question — html questions don't store values
    // in model.data, so read the html property directly from the question object.
    const workExclusionTextQuestion = model.getQuestionByName(
      'Munich_PersonalServices_9219_WorkExclusionText_WORLD_EN'
    );
    const preCompleteWorkExclusionText = workExclusionTextQuestion
      ? (workExclusionTextQuestion as { html?: string }).html
      : undefined;
    const surveyValidateResult = model.completeLastPage();
    if (!surveyValidateResult) {
      model.currentPage.focusFirstErrorQuestion();
      return;
    }

    const { data: modelData } = model;
    formatDateAnswers(modelData);

    // Strip fetchThirdJSONs — it's a trigger variable (Phase 3 filename), not a user answer
    delete modelData['fetchThirdJSONs'];

    // Keep this LOB's saved answers single-LOB — drop the sibling's coverage toggle.
    stripSiblingCoverage(modelData, lobType);

    const answersInfo = {
      applicationId,
      answers: {
        ...modelData,
        BusinessInformation_100_BusinessAddress_WORLD_EN:
          phase2Answers.BusinessInformation_100_BusinessAddress_WORLD_EN,
        BusinessInformation_100_CustomerInfo_WORLD_EN:
          phase2Answers.BusinessInformation_100_CustomerInfo_WORLD_EN,
        BusinessInformation_100_EffectiveDate_WORLD_EN:
          phase2Answers.BusinessInformation_100_EffectiveDate_WORLD_EN,
        BusinessInformation_100_CompanyName_WORLD_EN:
          phase2Answers.BusinessInformation_100_CompanyName_WORLD_EN,
        Munich_PersonalServices_9219_WorkExclusionListHtml_WORLD_EN: preCompleteWorkExclusionListHtml,
        Munich_PersonalServices_9219_WorkExclusionText_WORLD_EN: preCompleteWorkExclusionText
      },
      isBrokerFlow: !!(
        phase2Answers['BusinessInformation_Broker'] || !preCompleteMunichFlag
      ),
      reasonFlag: preCompleteReasonFlag
    };

    const { data: result } = await completeApplicationMutation({
      variables: {
        answersInfo,
        pageName: model.currentPage.name,
        // Current page belongs to the Phase 3 LOB JSON — that's the JSON the
        // user just completed and the row should be stamped with.
        jsonName: currentLobJsonFileName
      }
    });

    if (!result) throw new Error('completeApplicationData should be ready');

    const {
      errors,
      kind,
      applicationGroupId: resultApplicationGroupId
    } = result.completeApplication;
    if (errors.length > 0) throw new Error(errors.join(' - '));
    setCompletedApplicationGroupId(resultApplicationGroupId);

    switch (kind) {
      case ApplicationResultKind.OfferQuote:
        if (!isLastLob && !isEndorsement) {
          const nextIndex = currentLobIndex + 1;
          // Sibling Applications are now seeded upfront on the biz_coverage
          // "Next" click, so lobApplicationIds already contains every LOB by
          // the time we reach Phase 3. The lazy seedNextLob path remains as a
          // fallback for in-flight users whose biz_coverage submit ran before
          // the upfront-seeding deploy.
          // TODO: remove the seedNextLob fallback in the release following
          // upfront-seeding rollout — once no in-flight pre-deploy users
          // remain, lobApplicationIds is guaranteed complete here.
          let nextApplicationId = lobApplicationIds[nextIndex];
          if (!nextApplicationId) {
            nextApplicationId = await seedNextLob(
              resultApplicationGroupId,
              fetchThirdJSONs[nextIndex]
            );
          }
          setIsNavigatingToNextLob(true);
          const { data: groupData } = await apolloClient.query<
            GetApplicationGroupQuery,
            GetApplicationGroupQueryVariables
          >({
            query: GetApplicationGroupDocument,
            variables: { id: nextApplicationId },
            fetchPolicy: 'network-only'
          });
          const nextLobType =
            groupData?.getApplicationGroup?.applications.find(
              (a) => a.applicationId.toString() === nextApplicationId.toString()
            )?.lobType ?? '';
          history.push(`/${nextLobType}/${nextApplicationId}`, {
            ...lobShellState,
            lobApplicationIds:
              lobApplicationIds.length > nextIndex
                ? lobApplicationIds
                : [...lobApplicationIds, nextApplicationId],
            currentLobIndex: nextIndex
          });
          return;
        }
        setRedirectToQuote(true);
        break;
      case ApplicationResultKind.SendToBroker:
      case ApplicationResultKind.DeclineBusiness:
        setRedirectToQuote(false);
        break;
      default:
        throw new Error('Unreachable - unexpected Application result state');
    }
    setComplete(true);
  };

  return (
    <div className="h-full Commercial" data-testid="LobShell">
      <div className="survey-container">
        <Survey model={model} css={SurveyCSS} />
        <Navigation
          goBack={undefined}
          goNext={undefined}
          doComplete={doComplete}
        />
      </div>
    </div>
  );
};

export default LobShell;
